import { Box, Card, CardBody, Flex, Grid, Skeleton, Text } from '@chakra-ui/react';
import { ReactNode } from 'react';

import { statesArray } from '../../../../constants/states';
import { useSocialProofStatsQuery } from '../../../../gql/generated/graphql';
import { stripCountySuffix } from '../../../../utils/strings';

const MIN_STATE_COUNT = 100;
const MIN_MAKE_COUNT = 50;

type Props = {
  dealId?: string;
};

const StatePlate = ({ state }: { state: string }) => (
  <Box
    as="img"
    src={`/leaseend-com-images/license-plates/license-plate-${state}.svg`}
    alt={`${state} license plate`}
    h="full"
    maxW="110px"
    mr="auto"
  />
);

const StatRow = ({ value, label }: { value: string; label: ReactNode }) => (
  <>
    <Text fontSize="2xl" fontWeight="800" color="gray.800" lineHeight="1">
      {value}
    </Text>
    <Text
      fontSize="12px"
      color="gray.500"
      letterSpacing="wider"
      lineHeight="1.3"
      textTransform="uppercase"
    >
      {label}
    </Text>
  </>
);

const SocialProofCard = ({ dealId }: Props) => {
  const { data, loading, error } = useSocialProofStatsQuery({
    variables: { dealId: dealId ?? '' },
    skip: !dealId,
    fetchPolicy: 'network-only',
  });

  const stats = data?.socialProofStats;

  if (loading || (!loading && !stats?.customerState) || error || !dealId) return null;

  const customerFullState =
    statesArray.find((states) => states.value === stats?.customerState)?.name ??
    stats?.customerState ??
    '';

  const useStateCount =
    stats?.customerCountByState != null && stats.customerCountByState >= MIN_STATE_COUNT;
  const stateRowCount = useStateCount ? stats?.customerCountByState : stats?.allTimeFundedCount;
  const stateRowSubline = useStateCount ? `in ${customerFullState}` : 'nationwide';

  const showMakeRow =
    stats?.vehicleBrand != null &&
    stats?.nationalBrandCount != null &&
    stats.nationalBrandCount >= MIN_MAKE_COUNT;

  return (
    <Card borderRadius="24px">
      <Flex align="center" justify="space-between" px={7} py={3}>
        <Text
          fontSize={{ base: 'lg', md: 'xl' }}
          fontWeight="700"
          color="leaseEndBlue"
          letterSpacing="wider"
          w="50%"
          mr={2}
        >
          Did You Know?
        </Text>
        {loading ? (
          <Skeleton w="80px" h="36px" borderRadius="6px" />
        ) : (
          stats?.customerState && <StatePlate state={stats.customerState} />
        )}
      </Flex>
      <CardBody p={2}>
        <Grid
          bgColor="grayLightest"
          p={5}
          borderRadius="16px"
          templateColumns="max-content 1fr"
          gap={4}
          alignItems="center"
        >
          {stateRowCount != null && (
            <StatRow
              value={stateRowCount.toLocaleString('en-US')}
              label={
                <>
                  <Text as="span" display="block" fontWeight="bold">
                    Leases we've ended
                  </Text>
                  <Text as="span">{stateRowSubline}</Text>
                </>
              }
            />
          )}

          {showMakeRow && stats?.nationalBrandCount != null && (
            <StatRow
              value={stats.nationalBrandCount.toLocaleString('en-US')}
              label={
                <>
                  <Text as="span" display="block" fontWeight="bold">
                    {stats.vehicleBrand} leases we've ended
                  </Text>
                  <Text as="span">nationwide</Text>
                </>
              }
            />
          )}

          {stats?.trustpilotRating != null && stats?.trustpilotReviewCount != null && (
            <StatRow
              value={`${stats.trustpilotRating.toFixed(1)}/5`}
              label={
                <>
                  <Text as="span" display="block" fontWeight="bold">
                    Trustpilot Rating
                  </Text>
                  <Text as="span">
                    {stats.trustpilotReviewCount.toLocaleString('en-US')} Reviews
                  </Text>
                </>
              }
            />
          )}
        </Grid>
      </CardBody>
    </Card>
  );
};

export default SocialProofCard;
